import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";
import { Input } from "~/components/ui/input";
import { Checkbox } from "~/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { IconPicker } from "~/components/ui/icon-picker";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import { cn } from "~/lib/cn";
import { Textarea } from "~/components/ui/textarea";

interface BaseControlledProps<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
}

// Controlled Input
interface ControlledInputProps<T extends FieldValues>
  extends BaseControlledProps<T> {
  type?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function ControlledInput<T extends FieldValues>({
  control,
  name,
  type = "text",
  placeholder,
  className,
  disabled,
}: ControlledInputProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <Input
          {...field}
          type={type}
          placeholder={placeholder}
          className={className}
          disabled={disabled}
          error={!!fieldState.error}
          value={field.value ?? ""}
        />
      )}
    />
  );
}

// Controlled Textarea
interface ControlledTextareaProps<T extends FieldValues>
  extends BaseControlledProps<T> {
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  rows?: number;
  maxLength?: number;
}

export function ControlledTextarea<T extends FieldValues>({
  control,
  name,
  placeholder,
  className,
  disabled,
  rows,
  maxLength,
}: ControlledTextareaProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <Textarea
          {...field}
          value={field.value ?? ""}
          onValueChange={field.onChange}
          placeholder={placeholder}
          className={className}
          disabled={disabled}
          rows={rows}
          maxLength={maxLength}
          error={!!fieldState.error}
        />
      )}
    />
  );
}

// Controlled Select
interface ControlledSelectProps<T extends FieldValues>
  extends BaseControlledProps<T> {
  options: string[] | { value: string; label: string }[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function ControlledSelect<T extends FieldValues>({
  control,
  name,
  options,
  placeholder = "Select an option",
  className,
  disabled,
}: ControlledSelectProps<T>) {
  const normalizedOptions = options.map((opt) =>
    "string" === typeof opt ? { value: opt, label: opt } : opt,
  );

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <Select
          value={field.value ?? undefined}
          onValueChange={field.onChange}
          disabled={disabled}
        >
          <SelectTrigger className={className}>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {normalizedOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    />
  );
}

// Controlled Checkbox
interface ControlledCheckboxProps<T extends FieldValues>
  extends BaseControlledProps<T> {
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
}

export function ControlledCheckbox<T extends FieldValues>({
  control,
  name,
  children,
  className,
  disabled,
}: ControlledCheckboxProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <Checkbox
          checked={field.value ?? false}
          onCheckedChange={field.onChange}
          className={className}
          disabled={disabled}
        >
          {children}
        </Checkbox>
      )}
    />
  );
}

// Controlled Checkbox Group (for arrays)
interface ControlledCheckboxGroupProps<T extends FieldValues>
  extends BaseControlledProps<T> {
  options: { value: string; label: string }[];
  className?: string;
  disabled?: boolean;
}

export function ControlledCheckboxGroup<T extends FieldValues>({
  control,
  name,
  options,
  className,
  disabled,
}: ControlledCheckboxGroupProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        const values = (field.value as string[]) || [];

        return (
          <div className={cn("space-y-2", className)}>
            {options.map((option) => (
              <Checkbox
                key={option.value}
                checked={values.includes(option.value)}
                onCheckedChange={(checked) => {
                  const newValues = checked
                    ? [...values, option.value]
                    : values.filter((v) => v !== option.value);
                  field.onChange(newValues);
                }}
                disabled={disabled}
              >
                {option.label}
              </Checkbox>
            ))}
          </div>
        );
      }}
    />
  );
}

// Controlled Single Checkbox (for individual expertise checkboxes)
interface ControlledSingleCheckboxProps<T extends FieldValues>
  extends BaseControlledProps<T> {
  value: string;
  _currentValues?: string[];
  maxCount?: number;
  className?: string;
  disabled?: boolean;
}

export function ControlledSingleCheckbox<T extends FieldValues>({
  control,
  name,
  value,
  _currentValues = [],
  maxCount,
  className,
  disabled,
}: ControlledSingleCheckboxProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        const values = (field.value as string[]) || [];
        const isChecked = values.includes(value);
        const isMaxReached = Boolean(
          maxCount && values.length >= maxCount && !isChecked,
        );

        return (
          <Checkbox
            checked={isChecked}
            onCheckedChange={(checked) => {
              const newValues =
                true === checked
                  ? [...values, value]
                  : values.filter((v) => v !== value);
              field.onChange(newValues);
            }}
            disabled={disabled || isMaxReached}
            className={className}
          />
        );
      }}
    />
  );
}

// Controlled Advanced Checkbox Group (with descriptions and custom change logic)
interface ControlledAdvancedCheckboxGroupProps<T extends FieldValues>
  extends BaseControlledProps<T> {
  options: {
    value: string;
    label: string;
    description?: string;
    disabled?: boolean;
  }[];
  className?: string;
  disabled?: boolean;
  onValueChange?: (values: string[], changedValue: string) => void;
}

export function ControlledAdvancedCheckboxGroup<T extends FieldValues>({
  control,
  name,
  options,
  className,
  disabled,
  onValueChange,
}: ControlledAdvancedCheckboxGroupProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        const values = (field.value as string[]) || [];

        const handleChange = (optionValue: string, checked: boolean) => {
          const newValues = checked
            ? [...values, optionValue]
            : values.filter((v) => v !== optionValue);

          field.onChange(newValues);

          if (onValueChange) {
            onValueChange(newValues, optionValue);
          }
        };

        return (
          <div className={cn("space-y-3", className)}>
            {options.map((option) => (
              <Checkbox
                key={option.value}
                checked={values.includes(option.value)}
                onCheckedChange={(checked) =>
                  handleChange(option.value, true === checked)
                }
                disabled={disabled || option.disabled}
              >
                <div className="flex flex-col gap-0.5">
                  <span>{option.label}</span>
                  {option.description && (
                    <span className="text-sm text-muted-foreground">
                      {option.description}
                    </span>
                  )}
                </div>
              </Checkbox>
            ))}
          </div>
        );
      }}
    />
  );
}

// Controlled Radio Group
interface ControlledRadioGroupProps<T extends FieldValues>
  extends BaseControlledProps<T> {
  options: {
    value: string;
    label: string;
    description?: string;
  }[];
  className?: string;
  disabled?: boolean;
}

export function ControlledRadioGroup<T extends FieldValues>({
  control,
  name,
  options,
  className,
  disabled,
}: ControlledRadioGroupProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <RadioGroup
          value={field.value ?? ""}
          onValueChange={field.onChange}
          className={className}
          disabled={disabled}
        >
          {options.map((option) => (
            <RadioGroupItem
              key={option.value}
              value={option.value}
              label={option.label}
              description={option.description}
            />
          ))}
        </RadioGroup>
      )}
    />
  );
}

// Controlled IconPicker
interface ControlledIconPickerProps<T extends FieldValues>
  extends BaseControlledProps<T> {
  disabled?: boolean;
}

export function ControlledIconPicker<T extends FieldValues>({
  control,
  name,
  disabled,
}: ControlledIconPickerProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <IconPicker
          value={field.value ?? ""}
          onChange={field.onChange}
          onBlur={field.onBlur}
          disabled={disabled}
        />
      )}
    />
  );
}
